/**
 * RenameSnapshotDialog — edit the label of a user-initiated snapshot.
 *
 * Kept as its own small component (instead of reusing SaveSnapshotDialog)
 * so the copy can say "Rename" explicitly and the submit path calls the
 * PATCH endpoint — saving vs. renaming have distinct server invariants
 * (content-hash dedupe only applies to save), so muddling them would make
 * both flows harder to reason about.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { renameUserSnapshot } from '@/services/networkRestoreApi';
import { WORKBENCH_OUTLINE_BTN_MODAL } from '@/utils/workbenchOutlineButtonClassNames.js';

const LABEL_MAX = 60;

export default function RenameSnapshotDialog({
  open,
  networkId,
  entry,
  onClose,
  onRenamed,
}) {
  const [label, setLabel] = useState(entry?.label || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLabel(entry?.label || '');
    setError(null);
    setSubmitting(false);
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 30);
    return () => clearTimeout(t);
  }, [open, entry]);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const trimmed = label.trim();
      await renameUserSnapshot(
        networkId,
        String(entry.capturedAt),
        trimmed.length ? trimmed : null,
      );
      onRenamed?.();
    } catch (e) {
      setError(e?.message || 'Failed to rename snapshot');
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === 'Escape') {
      onClose?.();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="rename-snapshot-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-5 py-4">
              <h3 className="text-sm font-semibold text-gray-900">Rename snapshot</h3>
              <p className="mt-1 text-xs text-gray-600">
                Update the label shown in the Snapshots list. Leave empty to
                clear.
              </p>
            </div>

            <div className="space-y-3 px-5 py-4">
              <label className="block">
                <span className="text-xs font-medium text-gray-700">Label</span>
                <input
                  ref={inputRef}
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value.slice(0, LABEL_MAX))}
                  onKeyDown={onKeyDown}
                  maxLength={LABEL_MAX}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                <span className="mt-1 block text-[11px] text-gray-400">
                  {label.length}/{LABEL_MAX}
                </span>
              </label>

              {error && (
                <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className={WORKBENCH_OUTLINE_BTN_MODAL}
              >
                {submitting ? 'Saving…' : 'Save'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
